import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogIn, User, Lock, Heart, Car } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { initiateGoogleLogin, processGoogleCallback } from '../utils/googleAuth';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserLogin = ({ onLogin, lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    username: '', 
    email: '', 
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [processingGoogle, setProcessingGoogle] = useState(false);

  // Process Google OAuth callback on mount
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('code')) {
        setProcessingGoogle(true);
        try {
          const data = await processGoogleCallback();
          if (data) {
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            onLogin(data.user);
            toast.success('Welcome!');
            navigate('/');
          }
        } catch (error) {
          toast.error('Google sign-in failed');
        } finally {
          setProcessingGoogle(false);
        }
      }
    };

    handleGoogleCallback();
  }, [navigate, onLogin]);

  const handleGoogleSignIn = () => {
    initiateGoogleLogin();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        username: registerForm.username,
        email: registerForm.email,
        password: registerForm.password
      });
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Login | AAA of Miami"
        description="Login or create an account at AAA of Miami"
      />
      <div className="pt-20 min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-secondary)' }}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200 to-blue-300 rounded-full opacity-20 blur-3xl dark:opacity-10"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-yellow-200 to-yellow-300 rounded-full opacity-20 blur-3xl dark:opacity-10"></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
              <Car className="w-8 h-8" style={{ color: 'var(--text-primary)' }} />
            </div>
          </Link>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="user-login-title">
            {t('login.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>{t('login.subtitle')}</p>
        </div>

        {/* Login/Register Card */}
        <div className="rounded-2xl shadow-xl p-8" style={{ background: 'var(--bg-primary)' }}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6" style={{ background: 'var(--bg-tertiary)' }}>
              <TabsTrigger 
                value="login" 
                data-testid="login-tab"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                {t('login.signIn')}
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                {t('login.signUp')}
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('login.username')}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                    <Input
                      type="text"
                      placeholder={t('login.enterUsername')}
                      value={loginForm.username}
                      onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                      className="pl-10 h-11"
                      style={{ 
                        color: 'var(--text-primary)',
                        background: 'var(--bg-primary)',
                        borderColor: 'var(--border-color)'
                      }}
                      required
                      data-testid="login-username"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('login.password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                    <Input
                      type="password"
                      placeholder={t('login.enterPassword')}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                      className="pl-10 h-11"
                      style={{ 
                        color: 'var(--text-primary)',
                        background: 'var(--bg-primary)',
                        borderColor: 'var(--border-color)'
                      }}
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white font-medium" 
                  disabled={loading} 
                  data-testid="login-submit"
                >
                  {loading ? (
                    t('login.signingIn')
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      {t('login.signIn')}
                    </>
                  )}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: 'var(--border-color)' }}></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
                      Or continue with
                    </span>
                  </div>
                </div>

                <GoogleSignInButton onClick={handleGoogleSignIn} />

                <div className="text-center mt-4">
                  <Link to="/forgot-password" className="text-sm" style={{ color: 'var(--accent)' }}>
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('login.username')}</label>
                  <Input
                    type="text"
                    placeholder={t('login.enterUsername')}
                    value={registerForm.username}
                    onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                    className="h-11"
                    style={{ 
                      color: 'var(--text-primary)',
                      background: 'var(--bg-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                    required
                    data-testid="register-username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('login.email')}</label>
                  <Input
                    type="email"
                    placeholder={t('login.enterEmail')}
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                    className="h-11"
                    style={{ 
                      color: 'var(--text-primary)',
                      background: 'var(--bg-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                    required
                    data-testid="register-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('login.password')}</label>
                  <Input
                    type="password"
                    placeholder={t('login.minChars')}
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                    className="h-11"
                    style={{ 
                      color: 'var(--text-primary)',
                      background: 'var(--bg-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                    required
                    minLength={6}
                    data-testid="register-password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('login.confirmPassword')}</label>
                  <Input
                    type="password"
                    placeholder={t('login.reenterPassword')}
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                    className="h-11"
                    style={{ 
                      color: 'var(--text-primary)',
                      background: 'var(--bg-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                    required
                    data-testid="register-confirm-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white font-medium" 
                  disabled={loading} 
                  data-testid="register-submit"
                >
                  {loading ? t('login.creatingAccount') : t('login.createAccount')}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" style={{ borderColor: 'var(--border-color)' }}></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
                      Or continue with
                    </span>
                  </div>
                </div>

                <GoogleSignInButton onClick={handleGoogleSignIn} />
              </form>
            </TabsContent>
          </Tabs>

          {/* Benefits */}
          <div className="mt-8 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>{t('login.memberBenefits')}</p>
            <div className="space-y-2">
              <div className="flex items-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                <Heart className="w-4 h-4 mr-2 text-red-500" />
                {t('login.saveFavorites')}
              </div>
              <div className="flex items-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                <Car className="w-4 h-4 mr-2 text-blue-500" />
                {t('login.compareVehicles')}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm hover:opacity-80" style={{ color: 'var(--text-secondary)' }}>
              ← {t('login.continueAsGuest')}
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserLogin;
